import { motion } from 'framer-motion';
import { Upload, Search, Brain, FileCheck } from 'lucide-react';

interface AnalysisProgressProps {
  stage: number; // 0-3
}

const stages = [
  { label: 'Uploading...', icon: Upload },
  { label: 'Extracting biomarkers...', icon: Search },
  { label: 'Analyzing results...', icon: Brain },
  { label: 'Generating recommendations...', icon: FileCheck },
];

export function AnalysisProgress({ stage }: AnalysisProgressProps) {
  return (
    <div className="flex min-h-[60vh] items-center justify-center">
      <div className="w-full max-w-sm space-y-6">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-copper/10">
            <Brain className="h-8 w-8 text-copper" />
          </div>
          <h2 className="text-lg font-semibold text-navy">
            Analyzing Your Labs
          </h2>
          <p className="mt-1 text-sm text-gray-400">
            This usually takes about 30 seconds
          </p>
        </motion.div>

        <div className="space-y-3">
          {stages.map((s, i) => {
            const Icon = s.icon;
            const isActive = i === stage;
            const isCompleted = i < stage;
            const isPending = i > stage;

            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.15 }}
                className={`flex items-center gap-3 rounded-lg px-4 py-3 transition-all ${
                  isActive
                    ? 'bg-copper/5 ring-1 ring-copper/20'
                    : isCompleted
                      ? 'bg-emerald-50'
                      : 'bg-gray-50'
                }`}
              >
                {/* Icon */}
                <div
                  className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${
                    isActive
                      ? 'bg-copper/10'
                      : isCompleted
                        ? 'bg-emerald-100'
                        : 'bg-gray-200'
                  }`}
                >
                  {isActive ? (
                    <motion.div
                      animate={{ scale: [1, 1.15, 1] }}
                      transition={{
                        duration: 1.5,
                        repeat: Infinity,
                        ease: 'easeInOut',
                      }}
                    >
                      <Icon className="h-4 w-4 text-copper" />
                    </motion.div>
                  ) : (
                    <Icon
                      className={`h-4 w-4 ${
                        isCompleted ? 'text-emerald-600' : 'text-gray-400'
                      }`}
                    />
                  )}
                </div>

                {/* Label */}
                <span
                  className={`text-sm font-medium ${
                    isActive
                      ? 'text-copper'
                      : isCompleted
                        ? 'text-emerald-600'
                        : isPending
                          ? 'text-gray-400'
                          : 'text-gray-600'
                  }`}
                >
                  {isCompleted ? s.label.replace('...', '') + ' - Done' : s.label}
                </span>

                {/* Active pulse */}
                {isActive && (
                  <motion.div
                    className="ml-auto h-2 w-2 rounded-full bg-copper"
                    animate={{ opacity: [1, 0.3, 1] }}
                    transition={{
                      duration: 1,
                      repeat: Infinity,
                      ease: 'easeInOut',
                    }}
                  />
                )}
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
